import { ScenePreview } from "@cva/renderer";
import {
  aspectLabel,
  BRAND_STYLES,
  type BrandKit,
  type BrandTokens,
  buildLibraryTemplate,
  getPreset,
  type LibraryTemplate,
  libraryTemplateFormats,
  libraryTokens,
  type SceneDocument,
  TEMPLATE_TOPIC_LABELS,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ImageIcon, Layers, Palette, Play } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { orpc } from "../lib/api";
import { useOrgFonts } from "../lib/fonts";
import { useOrgRole } from "../lib/org";
import { canvasMeasure, tokenFaces, useFontsReady } from "../lib/text-measure";
import { cn, errorMessage } from "../lib/utils";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import { Field, Input, Select } from "./ui/input";

/**
 * The built-in template library (packages/shared/src/templates). Previews are
 * built in the browser with the kit of the project, so they show the
 * organization's colours, fonts and logo – exactly what "Verwenden" creates.
 */

export interface TemplateBrand {
  /** Null while the organization (or project) has no kit. */
  tokens: BrandTokens | null;
  logos: BrandKit["logos"];
  kitName: string | null;
  /** Word mark where there is no logo. */
  brandName: string;
  /** The organization's own fonts are registered (needed before measuring text). */
  fontsRegistered: boolean;
}

/** Kit of a project (or the organization's default kit) as used by library templates. */
export function useTemplateBrand(orgId: string, projectId?: string): TemplateBrand | undefined {
  const { registered } = useOrgFonts(orgId);
  const kit = useQuery(
    orpc.brand.effective.queryOptions({
      input: projectId ? { orgId, projectId } : { orgId },
    }),
  );
  const org = useQuery(orpc.orgs.get.queryOptions({ input: { orgId } }));
  return useMemo(
    () =>
      kit.data === undefined
        ? undefined
        : {
            tokens: kit.data?.tokens ?? null,
            logos: kit.data?.logos ?? [],
            kitName: kit.data?.name ?? null,
            // Kit names can be campaigns ("Sommer 2026"); the organization is the brand.
            brandName: org.data?.name ?? "",
            fontsRegistered: registered,
          },
    [kit.data, org.data?.name, registered],
  );
}

/**
 * The document "Verwenden" creates, built with exact text measurement once
 * the fonts are loaded (null until then), so preview and file match.
 */
export function useTemplateDocument(
  template: LibraryTemplate | null,
  brand: TemplateBrand | undefined,
  style: string | undefined,
  /** Video templates: build in another video format. */
  presetId?: string | undefined,
): SceneDocument | null {
  const faces = useMemo(
    () =>
      template ? tokenFaces(libraryTokens(template, { tokens: brand?.tokens ?? null, style })) : [],
    [template, brand?.tokens, style],
  );
  const ready = useFontsReady(faces, Boolean(template && brand?.fontsRegistered));
  return useMemo(
    () =>
      ready && template && brand
        ? buildLibraryTemplate(template, {
            tokens: brand.tokens,
            logos: brand.logos,
            brandName: brand.brandName,
            style,
            measure: canvasMeasure,
            presetId,
          })
        : null,
    [ready, template, brand, style, presetId],
  );
}

/** Renders once the element scrolls into view (the gallery has many previews). */
function useInView<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);
  return [ref, visible];
}

/** Stand-in for the video picture behind reel templates. */
function VideoUnderlay() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(160deg, #3f3f46 0%, #18181b 55%, #27272a 100%)",
      }}
    />
  );
}

/** Longest loop of an animated video preview (ms). */
const MAX_PREVIEW_LOOP = 20_000;

/**
 * Length of a video template's preview loop: until its last timed element
 * ends (numbered tips, chapters), at least long enough for the entrances.
 */
function previewLoopDuration(doc: SceneDocument): number {
  let end = 0;
  for (const element of Object.values(doc.elements)) {
    if (element.time) end = Math.max(end, element.time.end);
  }
  return Math.min(MAX_PREVIEW_LOOP, Math.max(4000, end + 800));
}

/** Playback position while `active`, looping over `duration` ms with a short pause. */
function useLoopTime(active: boolean, duration: number): number | undefined {
  const [time, setTime] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!active) {
      setTime(undefined);
      return;
    }
    const started = performance.now();
    let frame = 0;
    const loop = () => {
      const t = (performance.now() - started) % (duration + 700);
      setTime(Math.min(t, duration - 1));
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [active, duration]);
  return time;
}

/** Scaled preview of one page of a library template. */
export function LibraryTemplatePreview({
  template,
  brand,
  style,
  maxWidth,
  maxHeight,
  pageIndex = 0,
  presetId,
  playing = false,
}: {
  template: LibraryTemplate;
  brand: TemplateBrand;
  style?: string | undefined;
  maxWidth: number;
  maxHeight: number;
  pageIndex?: number;
  /** Video templates: preview in another video format. */
  presetId?: string | undefined;
  /** Video templates: play the animations (e.g. while the pointer is on the tile). */
  playing?: boolean;
}) {
  const doc = useTemplateDocument(template, brand, style, presetId);
  if (!doc) {
    const preset = getPreset(presetId ?? template.presetId);
    const ratio = preset ? preset.width / preset.height : 1;
    const width = Math.round(Math.min(maxWidth, maxHeight * ratio));
    return (
      <div className="animate-pulse bg-zinc-200/70" style={{ width, height: width / ratio }} />
    );
  }
  return (
    <TemplateDocumentPreview
      doc={doc}
      brand={brand}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      pageIndex={pageIndex}
      stillTime={template.previewTime ?? 0}
      playing={playing}
    />
  );
}

function TemplateDocumentPreview({
  doc,
  brand,
  maxWidth,
  maxHeight,
  pageIndex,
  stillTime,
  playing,
}: {
  doc: SceneDocument;
  brand: TemplateBrand;
  maxWidth: number;
  maxHeight: number;
  pageIndex: number;
  /** Video: moment the still preview shows. */
  stillTime: number;
  playing: boolean;
}) {
  const assets = useMemo(
    () =>
      new Map(
        brand.logos.map((logo) => [
          logo.asset.id,
          { url: logo.asset.url, width: logo.asset.width, height: logo.asset.height },
        ]),
      ),
    [brand.logos],
  );
  const ratio = doc.format.width / doc.format.height;
  const width = Math.round(Math.min(maxWidth, maxHeight * ratio));
  const scale = width / doc.format.width;
  const page = doc.pages[pageIndex] ?? doc.pages[0]!;
  const video = doc.kind === "video";
  const loop = useMemo(() => previewLoopDuration(doc), [doc]);
  const time = useLoopTime(video && playing, loop);
  return (
    <div className="relative shadow-sm ring-1 ring-black/5" style={{ width }}>
      <ScenePreview
        document={doc}
        pageIndex={pageIndex}
        width={width}
        resolveAsset={(id) => assets.get(id)}
        {...(video
          ? {
              time: time ?? stillTime,
              animate: time !== undefined,
              captionPlaceholder: "So sehen deine Untertitel aus",
              // Templates on black go over the video; coloured ones (mockups) show their page.
              ...(page.background === "#000000" ? { underlay: <VideoUnderlay /> } : {}),
            }
          : {})}
      />
      {/* Image placeholders: a small hint where a photo goes. */}
      {page.children.map((id) => {
        const element = doc.elements[id];
        if (element?.type !== "shape" || element.placeholder !== "image") return null;
        const size = Math.max(
          12,
          Math.min(28, Math.min(element.width, element.height) * scale * 0.2),
        );
        return (
          <span
            key={id}
            className="pointer-events-none absolute flex items-center justify-center text-black/30"
            style={{
              left: element.x * scale,
              top: element.y * scale,
              width: element.width * scale,
              height: element.height * scale,
            }}
          >
            <ImageIcon style={{ width: size, height: size }} strokeWidth={1.5} />
          </span>
        );
      })}
    </div>
  );
}

export function templateFormatLabel(template: LibraryTemplate): string {
  // Video templates work in every video format; the label names the one they were designed for.
  const preset = getPreset(template.presetId);
  if (preset && libraryTemplateFormats(template).length > 1) {
    return `Video ${aspectLabel(preset.width, preset.height)} · alle Formate`;
  }
  return preset?.label ?? template.presetId;
}

/** Choice of the video format for video templates (nothing for designs). */
export function TemplateFormatField({
  template,
  value,
  onChange,
}: {
  template: LibraryTemplate;
  value: string;
  onChange: (presetId: string) => void;
}) {
  const formats = libraryTemplateFormats(template);
  if (formats.length < 2) return null;
  return (
    <Field label="Format" hint="Die Vorlage wird für das gewählte Format neu angeordnet.">
      <Select value={value} onChange={(event) => onChange(event.target.value)}>
        {formats.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {aspectLabel(preset.width, preset.height)} · {preset.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

export function LibraryTemplateTile({
  template,
  brand,
  style,
  presetId,
  selected,
  onClick,
}: {
  template: LibraryTemplate;
  brand: TemplateBrand;
  style?: string | undefined;
  /** Video templates: preview in this video format instead of their own. */
  presetId?: string | undefined;
  selected?: boolean;
  onClick: () => void;
}) {
  const [ref, visible] = useInView<HTMLButtonElement>();
  // Video templates play their animations while pointed at (or focused).
  const [hover, setHover] = useState(false);
  const pages = template.pages.length;
  const video = getPreset(template.presetId)?.kind === "video";
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className={cn(
        "group overflow-hidden rounded-xl border bg-white text-left transition hover:border-brand-300 hover:shadow-md",
        selected ? "border-brand-500 ring-2 ring-brand-500/20" : "border-zinc-200",
      )}
    >
      <div className="relative flex h-44 items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-50 p-3">
        {visible ? (
          <LibraryTemplatePreview
            template={template}
            brand={brand}
            style={style}
            maxWidth={200}
            maxHeight={148}
            presetId={video ? presetId : undefined}
            playing={video && hover}
          />
        ) : null}
        {pages > 1 ? (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded bg-zinc-900/70 px-1.5 py-0.5 text-[11px] font-medium text-white">
            <Layers className="size-3" /> {pages} Seiten
          </span>
        ) : null}
        {video ? (
          <span
            className={cn(
              "absolute top-2 right-2 inline-flex items-center gap-1 rounded bg-zinc-900/70 px-1.5 py-0.5 text-[11px] font-medium text-white transition-opacity",
              hover && "opacity-0",
            )}
          >
            <Play className="size-3" /> Video
          </span>
        ) : null}
      </div>
      <div className="border-t border-zinc-100 px-3 py-2.5">
        <p className="truncate text-sm font-medium text-zinc-900">{template.title}</p>
        <p className="truncate text-xs text-zinc-500">
          {templateFormatLabel(template)} · {TEMPLATE_TOPIC_LABELS[template.topic]}
        </p>
      </div>
    </button>
  );
}

/** Style chips: how the library looks while there is no kit. */
export function StylePicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (style: string | undefined) => void;
}) {
  const options: { id: string | undefined; label: string; colors?: string[] }[] = [
    { id: undefined, label: "Vorlagen-Original" },
    ...BRAND_STYLES.map((style) => ({
      id: style.id,
      label: style.label,
      colors: [style.colors.primary, style.colors.accent, style.colors.background],
    })),
  ];
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option.id ?? "original"}
          type="button"
          onClick={() => onChange(option.id)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium text-zinc-700 transition hover:border-zinc-300",
            value === option.id
              ? "border-zinc-900 bg-zinc-900 text-white hover:border-zinc-900"
              : "border-zinc-200 bg-white",
          )}
        >
          {option.colors ? (
            <span className="flex -space-x-1">
              {option.colors.map((color) => (
                <span
                  key={color}
                  className="size-3 rounded-full ring-1 ring-black/10"
                  style={{ background: color }}
                />
              ))}
            </span>
          ) : null}
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Explains where the look of the previews comes from. */
export function TemplateBrandNote({
  orgId,
  brand,
  children,
}: {
  orgId: string;
  brand: TemplateBrand | undefined;
  children?: ReactNode;
}) {
  if (!brand) return null;
  if (brand.tokens) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-600">
        <span className="flex -space-x-1">
          {brand.tokens.colors.slice(0, 5).map((color) => (
            <span
              key={color.key}
              className="size-5 rounded-full ring-2 ring-white"
              style={{ background: color.color }}
            />
          ))}
        </span>
        <span>
          Die Vorlagen erscheinen in deiner Marke{" "}
          <strong className="font-medium text-zinc-900">„{brand.kitName}“</strong> – mit Farben,
          Schriften und Logo. Neue Dateien bleiben mit dem Brand Kit verknüpft.
        </span>
        <Link
          to="/o/$orgId/brand"
          params={{ orgId }}
          className="ml-auto font-medium text-brand-700 hover:underline"
        >
          Marke bearbeiten
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-xl border border-zinc-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-start gap-3 text-sm text-zinc-600">
        <Palette className="mt-0.5 size-4 shrink-0 text-brand-600" />
        <p className="min-w-0 flex-1">
          Noch kein Brand Kit: Die Vorlagen zeigen Beispiel-Stile. Mit einem Brand Kit erscheinen
          sie automatisch in deinen Farben, Schriften und mit deinem Logo.
        </p>
        <Link
          to="/o/$orgId/brand"
          params={{ orgId }}
          className="font-medium text-brand-700 hover:underline"
        >
          Brand Kit anlegen
        </Link>
      </div>
      {children}
    </div>
  );
}

/** "Use template": title and project (creates a first project if there is none). */
export function UseLibraryTemplateDialog({
  orgId,
  template,
  style,
  defaultProjectId,
  defaultPresetId,
  onClose,
}: {
  orgId: string;
  template: LibraryTemplate;
  style?: string | undefined;
  defaultProjectId?: string | undefined;
  /** Video templates: format chosen at first (e.g. the gallery's preview format). */
  defaultPresetId?: string | undefined;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useOrgRole(orgId);
  const projects = useQuery(
    orpc.projects.list.queryOptions({ input: { orgId, includeArchived: false } }),
  );
  const [projectId, setProjectId] = useState(defaultProjectId ?? "");
  const [projectName, setProjectName] = useState("Mein erstes Projekt");
  const [title, setTitle] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [formatId, setFormatId] = useState(() =>
    defaultPresetId && libraryTemplateFormats(template).some((f) => f.id === defaultPresetId)
      ? defaultPresetId
      : template.presetId,
  );
  useEffect(() => {
    if (!projectId && projects.data?.[0]) setProjectId(projects.data[0].id);
  }, [projectId, projects.data]);
  const noProjects = projects.data?.length === 0;
  const brand = useTemplateBrand(orgId, projectId || undefined);
  // Without a kit the chosen style decides the look; with a kit the kit does.
  const effectiveStyle = brand?.tokens ? undefined : style;
  const content = useTemplateDocument(template, brand, effectiveStyle, formatId);
  const createProject = useMutation(orpc.projects.create.mutationOptions());
  const createDocument = useMutation(orpc.documents.create.mutationOptions());
  const busy = createProject.isPending || createDocument.isPending;

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const targetProject = noProjects
        ? (await createProject.mutateAsync({ orgId, name: projectName.trim() || "Projekt" })).id
        : projectId;
      // Built in the browser with exact text measurement, so the file matches
      // the preview. A new first project uses the default kit, like the preview.
      if (!targetProject || !content) return;
      const document = await createDocument.mutateAsync({
        orgId,
        projectId: targetProject,
        content,
        title: title.trim() || template.title,
      });
      await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
      onClose();
      await navigate({
        to: "/o/$orgId/documents/$documentId",
        params: { orgId, documentId: document.id },
      });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`„${template.title}“ verwenden`}
        description={template.description}
        className="max-w-2xl"
      >
        <form onSubmit={submit} className="grid gap-6 sm:grid-cols-[1fr_16rem]">
          <div className="space-y-4">
            <Field label="Titel">
              <Input
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={template.title}
              />
            </Field>
            {noProjects ? (
              <Field
                label="Neues Projekt"
                hint="Noch gibt es kein Projekt – die Datei landet in diesem neuen Projekt."
              >
                <Input
                  required
                  value={projectName}
                  onChange={(event) => setProjectName(event.target.value)}
                  disabled={!can("project.create")}
                />
              </Field>
            ) : (
              <Field label="Projekt" hint="Die Datei übernimmt das Brand Kit dieses Projekts.">
                <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                  {projects.data?.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <TemplateFormatField template={template} value={formatId} onChange={setFormatId} />
            <ul className="space-y-1.5 text-sm text-zinc-600">
              <li>
                Format:{" "}
                <span className="text-zinc-900">
                  {getPreset(formatId)?.label ?? templateFormatLabel(template)}
                </span>
                {template.pages.length > 1 ? ` · ${template.pages.length} Seiten` : ""}
              </li>
              <li>
                {brand?.tokens
                  ? "Farben, Schriften und Logo kommen aus dem Brand Kit und bleiben verknüpft."
                  : "Ohne Brand Kit übernimmt die Datei die Farben und Schriften der Vorschau."}
              </li>
              <li>Texte anklicken und ändern, Fotos einfach auf die Bildflächen ziehen.</li>
            </ul>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-72 w-64 items-center justify-center rounded-lg bg-zinc-100 p-3">
              {brand ? (
                <LibraryTemplatePreview
                  template={template}
                  brand={brand}
                  style={effectiveStyle}
                  maxWidth={232}
                  maxHeight={264}
                  pageIndex={pageIndex}
                  presetId={formatId}
                  playing
                />
              ) : null}
            </div>
            {template.pages.length > 1 ? (
              <div className="flex gap-1">
                {template.pages.map((page, index) => (
                  <button
                    // biome-ignore lint/suspicious/noArrayIndexKey: pages have no ids
                    key={index}
                    type="button"
                    onClick={() => setPageIndex(index)}
                    aria-label={page.name ?? `Seite ${index + 1}`}
                    className={cn(
                      "size-2.5 rounded-full bg-zinc-300",
                      index === pageIndex && "bg-zinc-900",
                    )}
                  />
                ))}
              </div>
            ) : null}
          </div>
          <div className="sm:col-span-2">
            <DialogFooter>
              <Button
                type="submit"
                loading={busy}
                disabled={
                  !content ||
                  !can("document.create") ||
                  (noProjects ? !can("project.create") : !projectId)
                }
              >
                Datei anlegen
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
